import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from '../common/useDebouncedCallback';
import { useCallbackRef } from '../common/useCallbackRef';
import { cpGpMatchClient } from '../../../infrastructure/CpGpMatchClient';
import { glowpickClient } from '../../../infrastructure/GlowpickClient';
import { BatchClient, CommandName } from '../../../infrastructure/BatchClient';
import type { ExportCpGpMatchedArgs } from '../../../infrastructure/BatchClient';
import { getConfig, Stage } from '../../../config';
import type { CpGpMatchedProduct, ScrapingStatus } from '../../../models/Coupang';
import type { GlowpickProduct } from '../../../models/Glowpick';
import { flushSync } from 'react-dom';
interface PaginationInfo {
  hasNextPage: boolean;
  lastEvaluatedKey: string | undefined;
  currentPage: number;
  itemsPerPage: number;
}

type OutOfStockFilter = 'all' | 'inStock' | 'outOfStock';
type ThirdPartySellerFilter = 'all' | 'brand' | 'thirdParty';

export interface MatchedProductsFilters {
  profile: string; // operator/user name
  dateFrom: string; // ISO date string
  dateTo: string; // ISO date string
  scrapingStatus: ScrapingStatus | ''; // empty string for 'all'
  category: string;
  subcategory: string;
  cpProductId: string; // search by CP Product ID
  cpItemId: string; // search by CP Item ID
  outOfStock: OutOfStockFilter; //  'empty string for all'
  thirdPartySeller: ThirdPartySellerFilter; // empty string for 'all'
}

export const useMatchedProducts = () => {
  const config = getConfig();
  const batchClient = useMemo(() => new BatchClient(config.apiUrl), [config.apiUrl]);
  const initialDataLoaded = useRef(false);

  // Add guard against multiple concurrent calls (React StrictMode protection)
  const fetchingData = useRef(false);

  // Fetch categories using React Query
  const categoriesQuery = useQuery({
    queryKey: ['glowpick', 'categories'],
    queryFn: async () => {
      const response = await glowpickClient.getAllCategories();
      return response;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - categories don't change often
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
  });

  // Load cached products from sessionStorage on mount
  const [matchedProducts, setMatchedProducts] = useState<CpGpMatchedProduct[]>(() => {
    try {
      const cached = sessionStorage.getItem('matchedProducts.data');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [allMatchedProducts, setAllMatchedProducts] = useState<CpGpMatchedProduct[]>(() => {
    try {
      const cached = sessionStorage.getItem('matchedProducts.allData');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.info(`📦 Restored ${parsed.length} products from allMatchedProducts cache`);
        return parsed;
      }
      console.info('📦 No allMatchedProducts cache found, starting with empty array');
      return [];
    } catch (e) {
      console.error('❌ Failed to parse allMatchedProducts cache:', e);
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>(() => {
    // Try to restore pagination from sessionStorage
    try {
      const cached = sessionStorage.getItem('matchedProducts.pagination');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.info('📦 Restored pagination from cache:', {
          currentPage: parsed.currentPage,
          itemsPerPage: parsed.itemsPerPage,
          hasNextPage: parsed.hasNextPage,
        });
        return parsed;
      }
    } catch {
      // If parsing fails, use default
    }
    console.info('📦 No pagination cache, using defaults');
    return {
      hasNextPage: false,
      currentPage: 1,
      itemsPerPage: 20,
      lastEvaluatedKey: undefined,
    };
  });

  // Filter state - load from sessionStorage on mount
  const [filters, setFilters] = useState<MatchedProductsFilters>(() => {
    const savedFilters = sessionStorage.getItem('matchedProducts.filters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch {
        return {
          profile: '',
          dateFrom: '',
          dateTo: '',
          scrapingStatus: '',
          category: '',
          subcategory: '',
          cpProductId: '',
          cpItemId: '',
          outOfStock: 'all',
          thirdPartySeller: 'all',
        };
      }
    }
    return {
      profile: '',
      dateFrom: '',
      dateTo: '',
      scrapingStatus: '',
      category: '',
      subcategory: '',
      cpProductId: '',
      cpItemId: '',
      outOfStock: 'all',
      thirdPartySeller: 'all',
    };
  });

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem('matchedProducts.filters', JSON.stringify(filters));
  }, [filters]);

  // Save pagination to sessionStorage whenever it changes
  useEffect(() => {
    console.info('💾 Saving pagination to cache:', {
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    });
    sessionStorage.setItem('matchedProducts.pagination', JSON.stringify(pagination));
  }, [pagination]);

  // Debounced filter state for ID searches to prevent excessive API calls
  const [debouncedFilters, setDebouncedFilters] = useState<Pick<MatchedProductsFilters, 'cpProductId' | 'cpItemId'>>(
    () => {
      // Initialize from cached filters if available
      try {
        const cached = sessionStorage.getItem('matchedProducts.filters');
        if (cached) {
          const parsed = JSON.parse(cached);
          return {
            cpProductId: parsed.cpProductId || '',
            cpItemId: parsed.cpItemId || '',
          };
        }
      } catch {
        // If parsing fails, use defaults
      }
      return {
        cpProductId: '',
        cpItemId: '',
      };
    },
  );

  // Debounced callback to update debounced filters (500ms delay)
  const updateDebouncedFilters = useDebouncedCallback(
    (newFilters: Pick<MatchedProductsFilters, 'cpProductId' | 'cpItemId'>) => {
      setDebouncedFilters(newFilters);
    },
    500,
  );

  // Track previous filters to detect actual changes (not just restoration from cache)
  const previousFiltersRef = useRef<string>('');

  // Extract unique profiles/operators from matched products
  const availableProfiles = useMemo(() => {
    const profiles = new Set<string>();
    allMatchedProducts.forEach((match) => {
      const cpUniqueId = `${match.cpProductId}-${match.cpItemId}`;
      const cpProducts = match.gpDetailedProduct?.metadata?.cpProducts;
      const matchingProduct = cpProducts?.find((product) => product.cpUniqueId === cpUniqueId);
      if (matchingProduct?.operator) {
        profiles.add(matchingProduct.operator);
      }
    });
    return Array.from(profiles).sort();
  }, [allMatchedProducts]);

  const availableCategories = useMemo(() => {
    return categoriesQuery.data?.categories || [];
  }, [categoriesQuery.data?.categories]);

  // Get subcategories from API based on selected category
  const availableSubcategories = useMemo(() => {
    if (!filters.category || !categoriesQuery.data?.subcategories[filters.category]) {
      return [];
    }
    return categoriesQuery.data.subcategories[filters.category];
  }, [filters.category, categoriesQuery.data?.subcategories]);

  // Since filtering is now done server-side, we use allMatchedProducts directly
  const filteredProducts = useMemo(() => {
    console.info(`🔍 Computing filteredProducts from ${allMatchedProducts.length} allMatchedProducts`);
    let filtered = [...allMatchedProducts];

    // Profile filter
    if (filters.profile) {
      filtered = filtered.filter((match) => {
        const cpUniqueId = `${match.cpProductId}-${match.cpItemId}`;
        const cpProducts = match.gpDetailedProduct?.metadata?.cpProducts;
        const matchingProduct = cpProducts?.find((product) => product.cpUniqueId === cpUniqueId);
        return matchingProduct?.operator === filters.profile;
      });
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter((match) => {
        if (!match.createdAt) {
          return false;
        }
        const matchDate = new Date(match.createdAt);
        return matchDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter((match) => {
        if (!match.createdAt) {
          return false;
        }
        const matchDate = new Date(match.createdAt);
        return matchDate <= toDate;
      });
    }

    // Scraping status filter
    if (filters.scrapingStatus) {
      filtered = filtered.filter((match) => match.scrapingStatus === filters.scrapingStatus);
    }

    // Out of stock filter
    filtered = (() => {
      if (filters.outOfStock === 'outOfStock') {
        return filtered.filter((match) => match.cpDetailedProduct?.outOfStock === true);
      }
      if (filters.outOfStock === 'inStock') {
        return filtered.filter((match) => match.cpDetailedProduct?.outOfStock === false);
      }
      return filtered;
    })();

    filtered = (() => {
      if (filters.thirdPartySeller === 'thirdParty') {
        return filtered.filter((match) => !match.cpDetailedProduct?.brand);
      }
      if (filters.thirdPartySeller === 'brand') {
        return filtered.filter((match) => match.cpDetailedProduct?.brand);
      }
      return filtered;
    })();

    return filtered;
  }, [allMatchedProducts, filters]);

  // Update displayed products when filtered products or pagination changes
  useEffect(() => {
    // When filters are active, show filtered products up to current "page" worth
    const maxItems = pagination.currentPage * pagination.itemsPerPage;
    const displayedProducts = filteredProducts.slice(0, maxItems);
    console.info(
      `📊 Updating matchedProducts: currentPage=${pagination.currentPage}, itemsPerPage=${pagination.itemsPerPage}, maxItems=${maxItems}, filteredCount=${filteredProducts.length}, displayedCount=${displayedProducts.length}`,
    );
    setMatchedProducts(displayedProducts);
  }, [filteredProducts, pagination.currentPage, pagination.itemsPerPage]);

  // Save data to sessionStorage whenever it changes (with size limit check)
  useEffect(() => {
    try {
      // Only cache if reasonable size (< 5MB)
      const dataStr = JSON.stringify(matchedProducts);
      if (dataStr.length < 5 * 1024 * 1024) {
        sessionStorage.setItem('matchedProducts.data', dataStr);
      }
    } catch (e) {
      console.warn('Failed to cache matched products:', e);
    }
  }, [matchedProducts]);

  useEffect(() => {
    try {
      const dataStr = JSON.stringify(allMatchedProducts);
      const sizeMB = (dataStr.length / (1024 * 1024)).toFixed(2);
      if (dataStr.length < 5 * 1024 * 1024) {
        sessionStorage.setItem('matchedProducts.allData', dataStr);
        console.info(`💾 Cached ${allMatchedProducts.length} products to allMatchedProducts (${sizeMB} MB)`);
      } else {
        console.warn(`⚠️ allMatchedProducts cache too large (${sizeMB} MB), skipping cache`);
      }
    } catch (e) {
      console.warn('Failed to cache all matched products:', e);
    }
  }, [allMatchedProducts]);

  // Helper function to get running export jobs
  const getRunningJobs = useCallback(async () => {
    try {
      const activeStatuses = ['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'];
      const batchResponse = await batchClient.listBatchJobs({
        commandName: CommandName.EXPORT_CP_GP_MATCHED,
      });

      // Filter jobs that have active statuses
      const activeJobs = batchResponse.jobs.filter((job) => activeStatuses.includes(job.status));
      return activeJobs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Failed to fetch running export jobs:', error);
      return [];
    }
  }, [batchClient]);

  // Google Sheets Export via Batch Job
  const exportToGoogleSheet = useCallback(
    async (selectedSubcategories?: string[]) => {
      // Validate that category is selected
      if (!filters.category) {
        throw new Error('Category must be selected to export');
      }

      setIsExporting(true);
      try {
        // Determine sheet name and limit based on environment
        let sheetName: string;
        let exportArgs: ExportCpGpMatchedArgs;

        const sheetId = '1cH8ceSlOgKoaZc_M7UP2iXZ4H6lWcZvfADj6MQgx_LI';

        // Use selected subcategories if provided, otherwise fall back to current filter
        const subCategoriesToExport =
          selectedSubcategories && selectedSubcategories.length > 0
            ? selectedSubcategories
            : filters.subcategory
              ? [filters.subcategory]
              : undefined;

        if (config.stage === Stage.PROD) {
          sheetName = 'CpGpMatched-' + new Date().toISOString();
          exportArgs = {
            sheetId,
            sheetName,
            category: filters.category,
            ...(subCategoriesToExport && { subCategories: subCategoriesToExport }),
          };
        } else {
          // Local, dev and staging environments
          const envPrefix = config.stage || 'test';
          sheetName = `${envPrefix}-CpGpMatched-${new Date().toISOString()}`;
          exportArgs = {
            sheetId,
            sheetName,
            category: filters.category,
            ...(subCategoriesToExport && { subCategories: subCategoriesToExport }),
          };
        }

        console.info(`🚀 Triggering ExportCpGpMatched batch job for ${config.stage} environment:`, exportArgs);

        await batchClient.triggerExportCpGpMatchedJob(exportArgs);

        console.info(
          `✅ ExportCpGpMatched batch job triggered successfully for category: ${filters.category}${subCategoriesToExport ? ` with ${subCategoriesToExport.length} subcategor${subCategoriesToExport.length === 1 ? 'y' : 'ies'}` : ''}`,
        );
      } catch (error) {
        console.error('Error triggering ExportCpGpMatched batch job:', error);
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    [batchClient, config.stage, filters.category, filters.subcategory],
  );

  const fetchMatchedProducts = useCallbackRef(async (limit?: number, lastEvaluatedKey?: string) => {
    // Don't fetch if both category and subcategory are not selected
    if (!filters.category || !filters.subcategory) {
      console.info('🔍 Skipping fetch - both category and subcategory must be selected');
      setAllMatchedProducts([]);
      setMatchedProducts([]);
      setLoading(false);
      return;
    }

    // Prevent multiple concurrent calls (React StrictMode protection)
    if (fetchingData.current) {
      console.info('🔍 Fetch already in progress, skipping duplicate call');
      return;
    }

    fetchingData.current = true;
    setLoading(true);
    try {
      let currentGpProductIds: string[] = [];
      let bulkGlowpickData: Record<string, GlowpickProduct> = {};
      const hasCategory = filters.category && filters.subcategory;

      // Only query if both category and subcategory are selected
      if (filters.category && filters.subcategory) {
        // Use the bulk endpoint to get all products for the category at once
        // This is more efficient than individual API calls
        const glowpickProductsByCategoriesResponse = await glowpickClient.getProductsByCategory(
          filters.category,
          filters.subcategory,
          2000, // Get all products at once
        );

        // Filter products that have CP matches and create a lookup map
        const matchedProducts = glowpickProductsByCategoriesResponse.products.filter(
          (product) => product.metadata?.cpProducts && product.metadata.cpProducts.length > 0,
        );

        console.info(
          `🔍 Found ${matchedProducts.length} products with CP matches for ${filters.category} > ${filters.subcategory}`,
        );

        // Create a lookup map for efficient access to Glowpick product data
        bulkGlowpickData = matchedProducts.reduce(
          (acc, product) => {
            acc[product.productId] = product;
            return acc;
          },
          {} as Record<string, GlowpickProduct>,
        );

        // For pagination, slice the matched products
        currentGpProductIds = matchedProducts
          .map((product) => product.productId)
          .slice(
            (pagination.currentPage - 1) * pagination.itemsPerPage,
            pagination.currentPage * pagination.itemsPerPage,
          );

        console.info(`🔍 Using ${currentGpProductIds.length} product IDs for page ${pagination.currentPage}`);
      }

      const _response = await cpGpMatchClient.getAllMatches(
        limit || pagination.itemsPerPage,
        lastEvaluatedKey,
        currentGpProductIds,

        debouncedFilters.cpProductId || undefined,
        debouncedFilters.cpItemId || undefined,
      );

      const response = (() => {
        if (!hasCategory) {
          return _response;
        }
        if (currentGpProductIds.length === 0 && pagination.currentPage === 1) {
          return {
            ..._response,
            matches: [],
            hasNextPage: false,
            lastEvaluatedKey: undefined,
          };
        }
        if (currentGpProductIds.length < pagination.itemsPerPage && pagination.currentPage >= 1) {
          return {
            ..._response,
            hasNextPage: false,
          };
        }
        return {
          ..._response,
          hasNextPage: true,
        };
      })();

      // Use only bulk Glowpick product data - no individual API calls needed
      const enrichedMatches = response.matches.map((match: CpGpMatchedProduct) => {
        // If we have bulk data for this product, use it
        const bulkGlowpickProduct = bulkGlowpickData[match.gpProductId];
        if (bulkGlowpickProduct) {
          console.info(`🔍 Using bulk data for product ${match.gpProductId}`);
          return {
            ...match,
            gpDetailedProduct: bulkGlowpickProduct,
          };
        } else {
          // No individual API calls - bulk API contains all necessary data
          console.info(`🔍 Product ${match.gpProductId} not in bulk data, using match data only`);
          return match;
        }
      });

      // Log details about what we received
      console.info(`🔍 Received ${response.matches.length} matches from server`);
      console.info(`🔍 After enrichment: ${enrichedMatches.length} items`);

      // Check for any null/undefined items
      const nullItems = enrichedMatches.filter((match) => !match);
      if (nullItems.length > 0) {
        console.warn(`⚠️ Found ${nullItems.length} null/undefined items`);
      }

      // For now, let's pass all items through and handle safety in the UI
      const validMatches = enrichedMatches;

      console.info(
        `✅ Server sent ${response.matches.length} items, enriched ${enrichedMatches.length}, displaying ${validMatches.length} valid items`,
      );

      if (pagination.currentPage > 1) {
        // Load More: append new products to existing ones
        setAllMatchedProducts((prev) => [...prev, ...validMatches]);
        setPagination((prev) => ({
          ...prev,
          hasNextPage: response.hasNextPage || false,
          lastEvaluatedKey: response.lastEvaluatedKey || undefined,
        }));
      } else {
        // Initial load or refresh: replace all products
        setAllMatchedProducts(validMatches);
        setPagination((prev) => ({
          ...prev,
          hasNextPage: response.hasNextPage || false,
          lastEvaluatedKey: response.lastEvaluatedKey || undefined,
          itemsPerPage: limit || prev.itemsPerPage,
        }));
      }

      console.info(`✅ Loaded ${validMatches.length} matched products with Glowpick details`);
    } catch (error) {
      console.error('Error fetching matched products:', error);
      setAllMatchedProducts([]);
      setMatchedProducts([]);
    } finally {
      setLoading(false);
      fetchingData.current = false; // Reset the guard
    }
  });

  const refresh = useCallbackRef(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1, lastEvaluatedKey: undefined }));
    fetchMatchedProducts(pagination.itemsPerPage);
  });

  //load initial data only if category and subcategory are selected
  useEffect(() => {
    if (!initialDataLoaded.current && filters.category && filters.subcategory) {
      // Check if we already have cached data for this category/subcategory
      const cachedFilters = sessionStorage.getItem('matchedProducts.filters');
      const cachedData = sessionStorage.getItem('matchedProducts.data');

      if (cachedData && cachedFilters) {
        try {
          const parsed = JSON.parse(cachedFilters);
          // If cached data matches current filters, skip the fetch
          if (parsed.category === filters.category && parsed.subcategory === filters.subcategory) {
            console.info('📦 Using cached data, skipping initial fetch');
            console.info(`📦 Cached: ${parsed.category} > ${parsed.subcategory}`);
            console.info(`📦 Current: ${filters.category} > ${filters.subcategory}`);
            initialDataLoaded.current = true;

            // Initialize previousFiltersRef to prevent unnecessary refresh
            previousFiltersRef.current = JSON.stringify({
              category: filters.category,
              subcategory: filters.subcategory,
              cpProductId: debouncedFilters.cpProductId,
              cpItemId: debouncedFilters.cpItemId,
              itemsPerPage: pagination.itemsPerPage,
            });
            return;
          }
        } catch {
          // If parsing fails, proceed with fetch
        }
      }

      console.info('🔄 No cached data found, fetching fresh data...');
      fetchMatchedProducts(pagination.itemsPerPage);
      initialDataLoaded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoaded, filters.category, filters.subcategory]);

  //load data when filters change (only if both category and subcategory are present)
  useEffect(() => {
    // Create a string representation of current filters
    const currentFiltersKey = JSON.stringify({
      category: filters.category,
      subcategory: filters.subcategory,
      cpProductId: debouncedFilters.cpProductId,
      cpItemId: debouncedFilters.cpItemId,
      itemsPerPage: pagination.itemsPerPage,
    });

    // Skip refresh if filters haven't actually changed
    if (previousFiltersRef.current === currentFiltersKey) {
      console.info('⏭️ Skipping filter change effect - no changes detected');
      return;
    }

    // Skip refresh on first mount - let the initial load effect handle it
    if (previousFiltersRef.current === '') {
      console.info('🎬 First mount - initializing filter tracking');
      previousFiltersRef.current = currentFiltersKey;
      return;
    }

    console.info('📝 Filter change detected:', {
      old: previousFiltersRef.current,
      new: currentFiltersKey,
    });

    // Update the ref for next comparison
    previousFiltersRef.current = currentFiltersKey;

    if (initialDataLoaded.current && filters.category && filters.subcategory) {
      console.info('🔄 Filters changed with valid category/subcategory, refreshing data...');
      refresh();
    } else if (initialDataLoaded.current && (!filters.category || !filters.subcategory)) {
      // Clear data when filters are explicitly removed
      console.info('🗑️ Clearing data due to missing category/subcategory');
      setAllMatchedProducts([]);
      setMatchedProducts([]);
    }
  }, [
    filters.category,
    pagination.itemsPerPage,
    filters.subcategory,
    debouncedFilters.cpProductId,
    debouncedFilters.cpItemId,
    refresh,
  ]);

  const loadMore = useCallbackRef(() => {
    const displayedCount = matchedProducts.length;
    const filteredCount = filteredProducts.length;
    const hasMoreClientSide = displayedCount < filteredCount;

    console.info(
      `📄 Load More: currentPage=${pagination.currentPage}, hasNextPage=${pagination.hasNextPage}, displayed=${displayedCount}, filtered=${filteredCount}, hasMoreClientSide=${hasMoreClientSide}`,
    );

    // Increment page (this will show more items via the useEffect)
    flushSync(() => {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }));
    });

    // Only fetch more from server if we don't have enough client-side data
    if (!hasMoreClientSide && pagination.hasNextPage) {
      console.info(`📄 Fetching more products from server...`);
      fetchMatchedProducts(pagination.itemsPerPage, pagination.lastEvaluatedKey);
    } else {
      console.info(`📄 Showing more from client-side data (no server fetch needed)`);
    }
  });

  const setItemsPerPage = useCallbackRef((newLimit: number) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: newLimit,
      currentPage: 1,
      lastEvaluatedKey: undefined,
    }));
    fetchMatchedProducts(newLimit);
  });

  // Filter update functions
  const updateFilter = useCallbackRef((key: keyof MatchedProductsFilters, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      // Reset subcategory when category changes
      if (key === 'category') {
        newFilters.subcategory = '';
      }
      return newFilters;
    });

    // Apply debounce for ID filters to prevent excessive filtering
    if (key === 'cpProductId' || key === 'cpItemId') {
      const currentDebouncedFilters = {
        cpProductId: key === 'cpProductId' ? value : filters.cpProductId,
        cpItemId: key === 'cpItemId' ? value : filters.cpItemId,
      };
      updateDebouncedFilters(currentDebouncedFilters);
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  });

  const clearFilters = useCallbackRef(() => {
    setFilters({
      profile: '',
      dateFrom: '',
      dateTo: '',
      scrapingStatus: '',
      category: '',
      subcategory: '',
      cpProductId: '',
      cpItemId: '',
      outOfStock: 'all',
      thirdPartySeller: 'all',
    });
    // Clear debounced filters immediately when clearing all filters
    setDebouncedFilters({
      cpProductId: '',
      cpItemId: '',
    });
    // Note: Pagination reset and data fetch will be handled by useEffect hooks
  });

  const clearClientFilters = useCallbackRef(() => {
    setFilters((prev) => ({
      ...prev,
      profile: '',
      dateFrom: '',
      dateTo: '',
      scrapingStatus: '',
      outOfStock: 'all',
      thirdPartySeller: 'all',
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  });

  // Calculate if there are more items to load
  const hasMoreToLoad = useMemo(() => {
    const displayedCount = matchedProducts.length;
    const filteredCount = filteredProducts.length;

    // Can load more if we have more filtered items to show, or more items from API
    return displayedCount < filteredCount || pagination.hasNextPage;
  }, [matchedProducts.length, filteredProducts.length, pagination.hasNextPage]);

  // Fetch data for multiple subcategories
  const fetchMultipleSubcategories = useCallbackRef(async (subcategories: string[]) => {
    if (!filters.category || subcategories.length === 0) {
      console.warn('Category or subcategories not provided');
      return;
    }

    setLoading(true);
    try {
      console.info(`🔍 Fetching data for ${subcategories.length} subcategories:`, subcategories);

      // Fetch data for each subcategory in parallel
      const promises = subcategories.map(async (subcategory) => {
        try {
          // First, get Glowpick products for this subcategory
          const glowpickProductsResponse = await glowpickClient.getProductsByCategory(
            filters.category,
            subcategory,
            2000,
          );

          const matchedProducts = glowpickProductsResponse.products.filter(
            (product) => product.metadata?.cpProducts && product.metadata.cpProducts.length > 0,
          );

          console.info(
            `✅ Found ${matchedProducts.length} Glowpick products with CP matches for subcategory: ${subcategory}`,
          );

          // Create lookup map
          const bulkGlowpickData: Record<string, GlowpickProduct> = {};
          matchedProducts.forEach((product) => {
            bulkGlowpickData[product.productId] = product;
          });

          // Get gpProductIds from matched products
          const currentGpProductIds = matchedProducts
            .map((product) => product.productId)
            .filter((id): id is string => Boolean(id));

          if (currentGpProductIds.length === 0) {
            console.info(`No matched products found for subcategory: ${subcategory}`);
            return [];
          }

          // Fetch CP-GP matches for these products
          const response = await cpGpMatchClient.getAllMatches(
            2000,
            undefined,
            currentGpProductIds,
            undefined,
            undefined,
          );

          console.info(`✅ Fetched ${response.matches.length} CP-GP matches for subcategory: ${subcategory}`);

          // Enrich matches with Glowpick data
          const enrichedMatches = response.matches
            .map((match: CpGpMatchedProduct) => {
              const gpProduct = bulkGlowpickData[match.gpProductId];
              return gpProduct ? { ...match, gpDetailedProduct: gpProduct } : match;
            })
            .filter((match: CpGpMatchedProduct | null): match is CpGpMatchedProduct => match !== null);

          return enrichedMatches;
        } catch (error) {
          console.error(`Error fetching subcategory ${subcategory}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const combinedResults = results.flat();

      // Remove duplicates based on cpProductId and cpItemId
      const uniqueResults = combinedResults.filter(
        (match: CpGpMatchedProduct, index: number, self: CpGpMatchedProduct[]) =>
          index === self.findIndex((m) => m.cpProductId === match.cpProductId && m.cpItemId === match.cpItemId),
      );

      console.info(`✅ Combined results: ${combinedResults.length} total, ${uniqueResults.length} unique matches`);

      setAllMatchedProducts(uniqueResults);
      setPagination((prev) => ({
        ...prev,
        hasNextPage: false,
        lastEvaluatedKey: undefined,
      }));
    } catch (error) {
      console.error('Error fetching multiple subcategories:', error);
      setAllMatchedProducts([]);
      setMatchedProducts([]);
    } finally {
      setLoading(false);
    }
  });

  return {
    matchedProducts,
    allMatchedProducts,
    loading,
    isExporting,
    pagination: {
      ...pagination,
      hasNextPage: hasMoreToLoad,
    },
    filters,
    availableProfiles,
    availableCategories,
    availableSubcategories,
    fetchMatchedProducts,
    fetchMultipleSubcategories,
    loadMore,
    refresh,
    setItemsPerPage,
    updateFilter,
    clearFilters,
    clearClientFilters,
    exportToGoogleSheet,
    getRunningJobs,
    categoriesLoading: categoriesQuery.isLoading,
    categoriesError: categoriesQuery.error,
  };
};
